import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Trash2, FileText, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { engageApi } from "@/lib/engageApi";
import { useI18n } from "@/lib/i18n";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function Templates() {
    const { t } = useI18n();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);

    useEffect(() => { load(); }, []);
    function load() {
        setLoading(true);
        engageApi.templates.list().then(setItems).catch(() => toast.error("Failed")).finally(() => setLoading(false));
    }

    async function handleDelete(id) {
        if (!window.confirm("Delete template?")) return;
        try {
            await engageApi.templates.remove(id);
            toast.success("Template deleted");
            load();
        } catch (_e) { toast.error("Failed"); }
    }

    return (
        <div className="px-6 lg:px-10 py-6">
            <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
                <div>
                    <h1 className="font-display font-black text-3xl tracking-tighter">{t.engage.templates.title}</h1>
                    <p className="mt-1 text-sm text-zinc-500">{t.engage.templates.subtitle}</p>
                </div>
                <button type="button" onClick={() => setShowForm(true)} data-testid="templates-create-btn" className="btn-cta text-white font-semibold text-sm px-4 py-2 rounded-full inline-flex items-center gap-2">
                    <Plus size={14} /> {t.engage.templates.create}
                </button>
            </div>

            <AnimatePresence>
                {showForm && (
                    <TemplateForm onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />
                )}
            </AnimatePresence>

            {loading ? (
                <div className="py-24 flex items-center justify-center text-zinc-500 text-sm">
                    <Loader2 size={18} className="animate-spin mr-2" /> Loading…
                </div>
            ) : items.length === 0 && !showForm ? (
                <div className="rounded-2xl p-10 text-center bg-brand-surface border border-white/8">
                    <div className="mx-auto w-14 h-14 rounded-2xl bg-gradient-to-br from-brand-indigo to-brand-violet flex items-center justify-center mb-3">
                        <FileText size={22} className="text-white" />
                    </div>
                    <h3 className="font-display font-bold text-xl">{t.engage.templates.empty}</h3>
                    <p className="mt-1 text-sm text-zinc-500 max-w-sm mx-auto">{t.engage.templates.emptyBody}</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {items.map((tpl) => (
                        <motion.div
                            key={tpl.template_id}
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="rounded-2xl p-5 bg-brand-surface border border-white/8 hover:border-white/20 transition group"
                            data-testid={`template-card-${tpl.template_id}`}
                        >
                            <div className="flex items-start justify-between">
                                <div className="min-w-0">
                                    <div className="text-white font-semibold truncate">{tpl.name}</div>
                                    <div className="mt-1 text-[10px] uppercase tracking-widest font-bold text-brand-cyan">{t.engage.templates.categories[tpl.category] || tpl.category}</div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => handleDelete(tpl.template_id)}
                                    data-testid={`template-delete-${tpl.template_id}`}
                                    className="opacity-0 group-hover:opacity-100 text-zinc-500 hover:text-rose-300 transition"
                                >
                                    <Trash2 size={14} />
                                </button>
                            </div>
                            <div className="mt-3 text-sm text-zinc-200 line-clamp-1">{tpl.subject}</div>
                            <div className="mt-2 text-xs text-zinc-500 line-clamp-3 whitespace-pre-wrap">{tpl.body}</div>
                        </motion.div>
                    ))}
                </div>
            )}
        </div>
    );
}

function TemplateForm({ onClose, onSaved }) {
    const { t } = useI18n();
    const [form, setForm] = useState({ name: "", subject: "", body: "", category: "general" });
    const [saving, setSaving] = useState(false);

    async function save() {
        if (!form.name.trim() || !form.subject.trim() || !form.body.trim()) {
            toast.error("Fill all fields"); return;
        }
        setSaving(true);
        try {
            await engageApi.templates.create(form);
            toast.success("Template saved");
            onSaved();
        } catch (_e) { toast.error("Failed"); }
        finally { setSaving(false); }
    }

    return (
        <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            data-testid="template-form"
            className="mb-6 rounded-2xl p-6 bg-brand-surface border border-white/10"
        >
            <div className="flex items-center justify-between mb-4">
                <div className="font-display font-bold text-lg">{t.engage.templates.create}</div>
                <button type="button" onClick={onClose} className="text-zinc-500 hover:text-white"><X size={16} /></button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="md:col-span-2">
                    <label className="text-xs text-zinc-400">{t.engage.templates.name}</label>
                    <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="template-name" className="mt-1 w-full px-3 py-2 rounded-lg bg-brand-void border border-white/10 text-sm outline-none focus:border-brand-indigo" />
                </div>
                <div>
                    <label className="text-xs text-zinc-400">{t.engage.templates.category}</label>
                    <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                        <SelectTrigger className="mt-1 bg-brand-void border-white/10 h-10" data-testid="template-category"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-brand-surface border-white/10 text-white">
                            {Object.entries(t.engage.templates.categories).map(([k, label]) => (
                                <SelectItem key={k} value={k}>{label}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </div>
            <div className="mt-3">
                <label className="text-xs text-zinc-400">{t.engage.templates.subject}</label>
                <input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="template-subject" className="mt-1 w-full px-3 py-2 rounded-lg bg-brand-void border border-white/10 text-sm outline-none focus:border-brand-indigo" />
            </div>
            <div className="mt-3">
                <label className="text-xs text-zinc-400">{t.engage.templates.body}</label>
                <textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} rows={6} data-testid="template-body" className="mt-1 w-full px-3 py-2 rounded-lg bg-brand-void border border-white/10 text-sm outline-none focus:border-brand-indigo resize-y font-mono" />
            </div>
            <div className="mt-6 flex justify-end gap-2">
                <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-zinc-300 hover:bg-white/5">{t.engage.templates.cancel}</button>
                <button type="button" onClick={save} disabled={saving} data-testid="template-save" className="btn-cta text-white font-semibold text-sm px-5 py-2 rounded-lg disabled:opacity-60">
                    {saving ? "…" : t.engage.templates.save}
                </button>
            </div>
        </motion.div>
    );
}
